import React from "react";
import { Award, Factory, Globe2, Users, Calendar, TrendingUp } from "lucide-react";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      <section className="border-b border-line relative overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-40 h-96 w-96 rounded-full bg-[#dd0447]/10 blur-[120px]" />
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <div className="overline mb-6">About AREL NEPAL</div>
          <h1 className="font-display max-w-4xl text-5xl font-black leading-[0.95] tracking-tight text-ink md:text-7xl">
            Nepal-first appliances,
            <br />
            <span className="text-[#dd0447]">engineered globally.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-warm">
            AREL Nepal Pvt Ltd is a Nepal-based home appliance company.
            <strong className="text-ink"> Established in 2010</strong>, we
            build on <strong className="text-ink">40+ years of combined
            group expertise</strong> in electronics and home appliances
            distribution across the region.
          </p>
        </div>
      </section>

      {/* Milestones */}
      <section className="mx-auto max-w-7xl px-6 pt-16 lg:px-10">
        <div className="grid gap-4 md:grid-cols-2">
          <div
            className="arel-card flex items-center gap-5 rounded-2xl p-6"
            data-testid="about-milestone-established"
          >
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#dd0447]/10 text-[#dd0447]">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-3xl font-black text-ink">
                Established in 2010
              </div>
              <div className="mt-1 text-sm text-muted-warm">
                A Nepali brand built for Nepali homes and shops.
              </div>
            </div>
          </div>
          <div
            className="arel-card flex items-center gap-5 rounded-2xl p-6"
            data-testid="about-milestone-expertise"
          >
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#dd0447]/10 text-[#dd0447]">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-3xl font-black text-ink">
                40 years of expertise
              </div>
              <div className="mt-1 text-sm text-muted-warm">
                Combined group / parent-company experience in home appliances.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <img
            src="/assets/lifestyle/all-appliances.jpeg"
            alt="AREL appliances lineup"
            className="aspect-[4/3] w-full rounded-2xl border border-line-strong object-cover"
          />
          <div>
            <div className="overline mb-4">Our Story · OEM Excellence</div>
            <h2 className="font-display text-4xl font-black tracking-tight text-ink">
              Built on OEM trust.
              <br />
              Delivered with Nepali warmth.
            </h2>
            <p className="mt-6 leading-relaxed text-muted-warm">
              AREL was founded in 2010 to bring premium, reliable home
              appliances to every Nepali family — without the hefty
              international brand price tag. We work directly with the most
              trusted <strong className="text-ink">OEM manufacturers</strong>{" "}
              in China and India to design and produce appliances specced for
              Nepal&apos;s power conditions, and backed by a growing dealer
              and service network.
            </p>
            <p className="mt-4 leading-relaxed text-muted-warm">
              Our OEM partnerships mean world-class engineering with the price
              advantage of direct manufacturing — passed on to Nepali
              families and shops. And as the official importer of Daikin Air
              Conditioners in Nepal, we also carry global-brand air
              conditioning for premium buyers.
            </p>
            <p className="mt-4 leading-relaxed text-muted-warm">
              From Terai heat to Himalayan winters, our appliances are built
              to last where you live.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-[#f4f1ec] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { icon: Factory, k: "5", v: "Product Categories" },
              { icon: Globe2, k: "2", v: "OEM Countries" },
              { icon: Users, k: "28+", v: "AREL SKUs · 5 Daikin" },
              { icon: Award, k: "10yr*", v: "Compressor Warranty" },
            ].map((s, i) => (
              <div key={i} className="arel-card rounded-xl p-6" data-testid={`about-stat-${i}`}>
                <s.icon className="h-6 w-6 text-[#dd0447]" />
                <div className="mt-6 font-display text-4xl font-black text-ink">{s.k}</div>
                <div className="mt-2 text-sm text-muted-warm">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="overline mb-4">Our promise</div>
        <h2 className="font-display max-w-3xl text-4xl font-black tracking-tight text-ink md:text-5xl">
          Quality you can measure. Service you can call.
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Rigorously tested",
              body: "Every product is voltage-tested, heat-tested and monsoon-tested for Nepali households.",
            },
            {
              title: "Nepal service network",
              body: "Growing after-sales network across cities — parts, service and support in your language.",
            },
            {
              title: "Honest pricing",
              body: "OEM economics mean premium spec without the international brand markup.",
            },
          ].map((c, i) => (
            <div key={i} className="arel-card rounded-xl p-8" data-testid={`about-promise-${i}`}>
              <div className="font-display text-xl font-bold text-ink">{c.title}</div>
              <p className="mt-3 text-sm leading-relaxed text-muted-warm">{c.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
